<template>
  <v-dialog :value="dialog" @input="$emit('update:dialog', $event)" max-width="560" scrollable>
    <v-card class="rounded-xl overflow-hidden">
      <v-toolbar flat color="primary" dark dense>
        <v-icon left small>mdi-palette-outline</v-icon>
        <v-toolbar-title class="subtitle-1 font-weight-bold">Categorías de armarios</v-toolbar-title>
        <v-spacer />
        <v-btn icon small @click="$emit('update:dialog', false)">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-4">
        <!-- Nueva / edición -->
        <div class="text-overline grey--text">{{ editandoId ? 'Editar categoría' : 'Nueva categoría' }}</div>
        <v-row dense align="center">
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.nombre"
              label="Nombre"
              outlined
              dense
              hide-details
              @keyup.enter="guardar"
            />
          </v-col>
          <v-col cols="12" sm="6" class="d-flex align-center">
            <v-menu offset-y :close-on-content-click="false" max-width="320">
              <template v-slot:activator="{ on, attrs }">
                <v-btn outlined v-bind="attrs" v-on="on" class="text-none px-2" aria-label="Símbolo y color">
                  <SimboloCategoria :icono="form.icono" :color="colorNormalizado()" :tamano="20" class="mr-2" />
                  Símbolo
                </v-btn>
              </template>
              <v-card class="pa-3">
                <div class="text-caption grey--text mb-1">Símbolo en los planos</div>
                <div class="rejilla-simbolos mb-3">
                  <button
                    v-for="s in simbolos"
                    :key="s.icono"
                    type="button"
                    class="opcion-simbolo"
                    :class="{ 'opcion-activa': form.icono === s.icono }"
                    :title="s.nombre"
                    :aria-label="`Símbolo ${s.nombre}`"
                    @click="form.icono = s.icono"
                  >
                    <SimboloCategoria :icono="s.icono" :color="colorNormalizado()" :tamano="22" />
                  </button>
                </div>
                <div class="text-caption grey--text mb-1">Color</div>
                <v-color-picker v-model="form.color" hide-inputs show-swatches :swatches="swatches" mode="hexa" width="100%" />
              </v-card>
            </v-menu>
            <v-btn color="primary" depressed class="ml-2" :disabled="!form.nombre.trim()" :loading="guardando" @click="guardar">
              {{ editandoId ? 'Guardar' : 'Añadir' }}
            </v-btn>
            <v-btn v-if="editandoId" icon small class="ml-1" title="Cancelar edición" @click="resetForm">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="my-4" />

        <!-- Listado -->
        <v-list v-if="categorias.length" dense>
          <v-list-item v-for="cat in categorias" :key="cat.id" class="px-1">
            <SimboloCategoria :icono="cat.icono" :color="cat.color" :tamano="20" class="mr-3" />
            <v-list-item-content>
              <v-list-item-title class="font-weight-medium">{{ cat.nombre }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action class="d-flex flex-row">
              <v-btn icon small @click="editar(cat)"><v-icon small color="orange">mdi-pencil-outline</v-icon></v-btn>
              <v-btn icon small @click="eliminar(cat)"><v-icon small color="error">mdi-delete-outline</v-icon></v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <div v-else class="text-center grey--text py-6">
          Todavía no hay categorías
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";
import SimboloCategoria from "@/components/utils/SimboloCategoria.vue";
import { SIMBOLOS, SIMBOLO_DEFECTO } from "@/utils/simbolosCategorias";

const API = `${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/clientes`;
const COLOR_DEFECTO = "#1E88E5";

export default {
  name: "DialogCategoriasArmarios",
  components: { SimboloCategoria },
  props: {
    dialog: { type: Boolean, default: false },
  },
  data() {
    return {
      form: { nombre: "", color: COLOR_DEFECTO, icono: SIMBOLO_DEFECTO },
      simbolos: SIMBOLOS,
      editandoId: null,
      guardando: false,
      swatches: [
        ["#E53935", "#D81B60"],
        ["#8E24AA", "#3949AB"],
        ["#1E88E5", "#00ACC1"],
        ["#43A047", "#7CB342"],
        ["#FDD835", "#FB8C00"],
        ["#6D4C41", "#546E7A"],
      ],
    };
  },
  computed: {
    ...mapState("armarios", ["categorias"]),
  },
  watch: {
    dialog(abierto) {
      if (!abierto) this.resetForm();
    },
  },
  methods: {
    resetForm() {
      this.form = { nombre: "", color: COLOR_DEFECTO, icono: SIMBOLO_DEFECTO };
      this.editandoId = null;
    },
    editar(cat) {
      this.editandoId = cat.id;
      this.form = { nombre: cat.nombre, color: cat.color, icono: cat.icono || SIMBOLO_DEFECTO };
    },
    // el color picker puede devolver #RRGGBBAA: nos quedamos con #RRGGBB
    colorNormalizado() {
      return String(this.form.color || COLOR_DEFECTO).slice(0, 7).toUpperCase();
    },
    async guardar() {
      const nombre = this.form.nombre.trim();
      if (!nombre) return;

      this.guardando = true;
      try {
        const body = { nombre, color: this.colorNormalizado(), icono: this.form.icono };
        if (this.editandoId) await axios.put(`${API}/categorias/${this.editandoId}`, body);
        else await axios.post(`${API}/categorias`, body);

        this.resetForm();
        this.$emit("cambiado");
      } catch (error) {
        console.error("Error al guardar categoría:", error);
        this.$emit("notificar", { text: "No se pudo guardar la categoría", color: "error" });
      } finally {
        this.guardando = false;
      }
    },
    async eliminar(cat) {
      const ok = await this.$confirmar({
        titulo: "Eliminar categoría",
        texto: `Se eliminará "${cat.nombre}". Sus armarios quedarán sin categoría.`,
        peligro: true,
      });
      if (!ok) return;
      try {
        await axios.delete(`${API}/categorias/${cat.id}`);
        if (this.editandoId === cat.id) this.resetForm();
        this.$emit("cambiado");
      } catch (error) {
        console.error("Error al eliminar categoría:", error);
        this.$emit("notificar", { text: "No se pudo eliminar la categoría", color: "error" });
      }
    },
  },
};
</script>

<style scoped>
.rejilla-simbolos {
  display: grid;
  grid-template-columns: repeat(auto-fill, 36px);
  gap: 4px;
}
.opcion-simbolo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 2px solid transparent;
  border-radius: 8px;
  background: #f5f7f9;
  cursor: pointer;
}
.opcion-simbolo:hover {
  background: #e8edf1;
}
.opcion-activa {
  border-color: #117f9c;
  background: white;
}
</style>
